// import { useMutation, useQueryClient } from "react-query";
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { client } from './api-client';

export const useCreateConversation = () => {
    const queryClient = useQueryClient();
    
    const createConversation = async (conversationData) => {
      const response = await client('conversations', {
        method: 'POST',
        data: conversationData,
        // Note: Keep as JSON since the image shows raw JSON body
      });
  
      if (response?.error) {
        throw new Error(response.error);
      }
  
      return response;
    };
  
    return useMutation(createConversation, {
      onSuccess: (data) => {
        console.log('✅ Conversation created successfully:', data);
        // Invalidate conversations list to refetch updated data
        queryClient.invalidateQueries(['conversations']);
      },
      onError: (error) => {
        console.error('❌ Failed to create conversation:', error.message);
      },
    });
};
  

  // Specific hook for user conversations with pagination
export const useUserConversations = (userId, options = {}) => {
    const { skip = 0, limit = 20, ...otherOptions } = options;
    
    return useQuery(
      ['conversations', 'user', userId, { skip, limit, ...otherOptions }],
      async () => {
        const searchParams = new URLSearchParams({
          user_id: userId,
          skip: skip.toString(),
          limit: limit.toString(),
          ...otherOptions
        });
        
        const response = await client(`conversations?${searchParams.toString()}`);
        
        if (response?.error) {
          throw new Error(response.error);
        }
        
        return response;
      },
      {
        enabled: !!userId, // Only run if userId is provided
        keepPreviousData: true,
      }
    );
};

// Get single conversation by ID
export const useGetConversation = (conversationId) => {
    return useQuery(
      ['conversations', conversationId],
      async () => {
        const response = await client(`conversations/${conversationId}`);
        
        if (response?.error) {
          throw new Error(response.error);
        }
        
        return response;
      },
      {
        enabled: !!conversationId,
        retry: 3,
      }
    );
  };

  // Get conversation messages with pagination
export const useGetConversationMessages = (conversationId, options = {}) => {
    const { skip = 0, limit = 50, ...otherOptions } = options;
    
    console.log('useGetConversationMessages called with:', { conversationId, options });
    
    return useQuery(
      ['conversations', conversationId, 'messages', { skip, limit, ...otherOptions }],
      async () => {
        const searchParams = new URLSearchParams({
          skip: skip.toString(),
          limit: limit.toString(),
          ...otherOptions
        });
        
        console.log('Making API call to:', `conversations/${conversationId}/messages?${searchParams.toString()}`);
        const response = await client(`conversations/${conversationId}/messages?${searchParams.toString()}`);
        console.log('Fetching messages for conversation:', response);
        if (response?.error) {
          throw new Error(response.error);
        }
        
        return response;
      },
      {
        enabled: !!conversationId && !!localStorage.getItem('token'),
        keepPreviousData: true,
        refetchOnWindowFocus: false, // Prevent automatic refetch on window focus
        refetchOnMount: false, // Only refetch when conversationId changes (handled by query key)
        refetchOnReconnect: false, // Prevent automatic refetch on reconnect
        onSuccess: (data) => {
          console.log('✅ Conversation messages fetched successfully:', data);
        },
        onError: (error) => {
          console.error('❌ Failed to fetch conversation messages:', error);
        },
      }
    );
  };


// Send message to conversation (chat endpoint)
export const useSendMessage = () => {
    const queryClient = useQueryClient();
    
    const sendMessage = async ({ conversationId, messageData }) => {
      const response = await client(`conversations/${conversationId}/chat`, {
        method: 'POST',
        data: messageData,
      });
      
      if (response?.error) {
        throw new Error(response.error);
      }
      
      return response;
    };
    
    return useMutation(sendMessage, {
      onSuccess: (data, variables) => {
        console.log('✅ Message sent successfully:', data);
        
        // Removed all automatic query invalidations to prevent messages API refetch
        // Messages will be updated via chat response data, not through separate API calls
        // queryClient.invalidateQueries(['conversations', variables.conversationId, 'messages']);
        // queryClient.invalidateQueries(['conversations', variables.conversationId]);
        // queryClient.invalidateQueries(['conversations']);
      },
      onError: (error) => {
        console.error('❌ Failed to send message:', error.message);
      },
    });
  };

// Send image message to conversation
// export const useSendImageMessage = () => {
//     const queryClient = useQueryClient();
    
//     const sendImageMessage = async ({ conversationId, imageFile, prompt }) => {
//       const formData = new FormData();
//       formData.append('image', imageFile);
//       formData.append('prompt', prompt);
      
//       const response = await client(`conversations/${conversationId}/image`, {
//         method: 'POST',
//         data: formData,
//         headers: {
//           'Content-Type': 'multipart/form-data',
//         },
//       });
      
//       if (response?.error) {
//         throw new Error(response.error);
//       }
      
//       return response;
//     };
    
//     return useMutation(sendImageMessage, {
//       onSuccess: (data, variables) => {
//         console.log('✅ Image message sent successfully:', data);
        
//         // Invalidate and refetch conversation messages
//         queryClient.invalidateQueries(['conversations', variables.conversationId, 'messages']);
        
//         // Optionally update the conversation's last activity
//         queryClient.invalidateQueries(['conversations', variables.conversationId]);
//         queryClient.invalidateQueries(['conversations']);
//       },
//       onError: (error) => {
//         console.error('❌ Failed to send image message:', error.message);
//       },
//     });
//   };

export const useSendImageMessage = () => {
  const queryClient = useQueryClient();

  const sendImageMessage = async ({ conversationId, imageFile, prompt }) => {
    const formData = new FormData();
    formData.append('image', imageFile);
    formData.append('prompt', prompt);

    const response = await client(`conversations/${conversationId}/image`, {
      method: 'POST',
      body: formData,   // ✅ use body, not data
      // ❌ remove Content-Type, browser sets it automatically
    });

    if (response?.error) {
      throw new Error(response.error);
    }

    return response;
  };

  return useMutation(sendImageMessage, {
    onSuccess: (data, variables) => {
      console.log('✅ Image message sent successfully:', data);

      // Removed all automatic query invalidations to prevent messages API refetch
      // Messages will be updated via image response data, not through separate API calls
      // queryClient.invalidateQueries(['conversations', variables.conversationId, 'messages']);
      // queryClient.invalidateQueries(['conversations', variables.conversationId]);
      // queryClient.invalidateQueries(['conversations']);
    },
    onError: (error) => {
      console.error('❌ Failed to send image message:', error.message);
    },
  });
};

export const useSendVoiceMessage = () => {
  const queryClient = useQueryClient();

  const sendVoiceMessage = async ({ conversationId, voiceFile, additionalData = {} }) => {
    const formData = new FormData();
    formData.append('voice', voiceFile);

    // Append any additional form data
    Object.keys(additionalData).forEach(key => {
      formData.append(key, additionalData[key]);
    });

    const response = await client(`conversations/${conversationId}/voice`, {
      method: 'POST',
      body: formData,   // ✅ use body, not data (consistent with image message)
      // ❌ remove Content-Type, browser sets it automatically with correct boundary
    });

    if (response?.error) {
      throw new Error(response.error);
    }

    return response;
  };

  return useMutation(sendVoiceMessage, {
    onSuccess: (data, variables) => {
      console.log('✅ Voice message sent successfully:', data);

      // Removed all automatic query invalidations to prevent messages API refetch
      // Messages will be updated via voice response data, not through separate API calls
      // queryClient.invalidateQueries(['conversations', variables.conversationId, 'messages']);
      // queryClient.invalidateQueries(['conversations', variables.conversationId]);
      // queryClient.invalidateQueries(['conversations']);
    },
    onError: (error) => {
      console.error('❌ Failed to send voice message:', error.message);
    },
  });
};




  // Update conversation
export const useUpdateConversation = () => {
    const queryClient = useQueryClient();
    
    const updateConversation = async ({ conversationId, updateData }) => {
      const response = await client(`conversations/${conversationId}`, {
        method: 'PUT',
        data: updateData,
      });
      
      if (response?.error) {
        throw new Error(response.error);
      }
      
      return response;
    };
    
    return useMutation(updateConversation, {
      onSuccess: (data, variables) => {
        console.log('✅ Conversation updated successfully:', data);
        
        // Update the specific conversation in cache
        queryClient.invalidateQueries(['conversations', variables.conversationId]);
        
        // Update conversations list
        queryClient.invalidateQueries(['conversations']);
      },
      onError: (error) => {
        console.error('❌ Failed to update conversation:', error.message);
      },
    });
  };


  // Delete conversation
export const useDeleteConversation = () => {
    const queryClient = useQueryClient();
    
    const deleteConversation = async (conversationId) => {
      const response = await client(`conversations/${conversationId}/clear`, {
        method: 'DELETE',
      });
      
      if (response?.error) {
        throw new Error(response.error);
      }
      
      return response;
    };
    
    return useMutation(deleteConversation, {
      onSuccess: (data, conversationId) => {
        console.log('✅ Conversation deleted successfully:', data);
        
        // Remove from cache
        queryClient.removeQueries(['conversations', conversationId]);
        
        // Invalidate conversations list
        queryClient.invalidateQueries(['conversations']);
      },
      onError: (error) => {
        console.error('❌ Failed to delete conversation:', error.message);
      },
    });
  };


  // Send voice message to conversation
// export const useSendVoiceMessage = () => {
//     const queryClient = useQueryClient();
    
//     const sendVoiceMessage = async ({ conversationId, voiceFile, additionalData = {} }) => {
//       const formData = new FormData();
//       formData.append('voice', voiceFile);
      
//       // Append any additional form data
//       Object.keys(additionalData).forEach(key => {
//         formData.append(key, additionalData[key]);
//       });
      
//       const response = await client(`conversations/${conversationId}/voice`, {
//         method: 'POST',
//         data: formData,
//         headers: {
//           'Content-Type': 'multipart/form-data',
//         },
//       });
      
//       if (response?.error) {
//         throw new Error(response.error);
//       }
      
//       return response;
//     };
    
//     return useMutation(sendVoiceMessage, {
//       onSuccess: (data, variables) => {
//         console.log('✅ Voice message sent successfully:', data);
        
//         // Invalidate and refetch conversation messages
//         queryClient.invalidateQueries(['conversations', variables.conversationId, 'messages']);
        
//         // Optionally update the conversation's last activity
//         queryClient.invalidateQueries(['conversations', variables.conversationId]);
//         queryClient.invalidateQueries(['conversations']);
//       },
//       onError: (error) => {
//         console.error('❌ Failed to send voice message:', error.message);
//       },
//     });
//   };
  



  
  


// OCR API for processing vehicle images and extracting information
export const useProcessVehicleImage = () => {
    const queryClient = useQueryClient();
    
    const processVehicleImage = async (imageFile) => {
      const formData = new FormData();
      formData.append('image', imageFile);
      
      const response = await client('OCR', {
        method: 'POST',
        data: formData,
        // Remove the Content-Type header to let the browser set it automatically with the correct boundary
        // headers: {
        //   'Content-Type': 'multipart/form-data',
        // },
      });
      
      if (response?.error) {
        throw new Error(response.error);
      }
      
      return response;
    };
    
    return useMutation(processVehicleImage, {
      onSuccess: (data) => {
        console.log('✅ Vehicle image processed successfully:', data);
        // The response contains: Car Name, VIN, License Plate, Year, Make, Model
      },
      onError: (error) => {
        console.error('❌ Failed to process vehicle image:', error.message);
      },
    });
  };

// Direct OCR API call without React Query (for non-component usage)
export const processVehicleImageDirect = async (imageFile) => {
  const formData = new FormData();
  formData.append('image', imageFile);
  
  const response = await client('OCR', {
    method: 'POST',
    data: formData,
    // Remove the Content-Type header to let the browser set it automatically with the correct boundary
    // headers: {
    //   'Content-Type': 'multipart/form-data',
    // },
  });
  
  if (response?.error) {
    throw new Error(response.error);
  }
  
  return response;
};

// Simple chat API for non-logged-in users (no conversation history)
export const useSimpleChat = () => {
  const sendSimpleChat = async (prompt) => {
    const response = await client('simple_chat', {
      method: 'POST',
      data: { prompt },
    });
    
    if (response?.error) {
      throw new Error(response.error);
    }
    
    return response;
  };
  
  return useMutation(sendSimpleChat, {
    onError: (error) => {
      console.error('❌ Failed to send simple chat message:', error.message);
    },
  });
};

// Get suggestions list
export const useGetSuggestions = (vehicle_id, swimlane) => {
  return useQuery(
    ['suggestions', vehicle_id, swimlane],
    async () => {
      const searchParams = new URLSearchParams();
      if (vehicle_id) {
        searchParams.append('vehicle_id', vehicle_id);
      }
      if (swimlane) {
        searchParams.append('swimlane', swimlane);
      }
      
      const queryString = searchParams.toString();
      const endpoint = queryString ? `suggestions?${queryString}` : 'suggestions';
      
      const response = await client(endpoint);
      
      if (response?.error) {
        throw new Error(response.error);
      }
      
      return response;
    },
    {
      retry: 2,
      enabled: !!swimlane, // Only fetch if swimlane is provided
      onSuccess: (data) => {
        console.log('✅ Suggestions fetched successfully:', data);
      },
      onError: (error) => {
        console.error('❌ Failed to fetch suggestions:', error);
      },
    }
  );
};

// Create suggestion
export const useCreateSuggestion = () => {
  const queryClient = useQueryClient();
  
  const createSuggestion = async ({ prompt, vehicle_id, swimlane }) => {
    const response = await client('suggestion', {
      method: 'POST',
      data: { 
        prompt,
        vehicle_id,
        swimlane
      },
    });
    
    if (response?.error) {
      throw new Error(response.error);
    }
    
    return response;
  };
  
  return useMutation(createSuggestion, {
    onSuccess: (data) => {
      console.log('✅ Suggestion created successfully:', data);
      // Invalidate suggestions list to refetch updated data
      queryClient.invalidateQueries(['suggestions']);
    },
    onError: (error) => {
      console.error('❌ Failed to create suggestion:', error.message);
    },
  });
};



